import { InfiniteData } from 'react-query/types/core/types';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query';
import { useCurrentActiveMember, useCurrentMember } from '../redux/selector';
import { managementApi, memberApi } from '../api';
import {
  CreateCommentRequest,
  CreatePostRequest,
  CreateReactionCommentRequest,
  HidePostRequest,
  InAppMessageType,
  PagePostAggregate,
  PagePostComment,
  Post,
  PostAggregate,
  PostComment,
  PostCommentType,
  PostTag,
  UpdateCommentRequest,
  UpdatePostRequest,
  CreateAckCommentRequest,
} from '@quick/client';
import {
  COMMENTS_QUERY_KEY,
  COMMUNITIES_KEY,
  DETAIL,
  getInfinitePagesContent,
  insertToInfinitePages,
  LIST,
  POSTS_QUERY_KEY,
} from '../api/query';
import { UseInfiniteQueryResult } from 'react-query/types/react/types';
import { useEffect } from 'react';
import { useInAppMessagesMutation } from './Notification';
import { dedupe, insertToList, replaceInList } from '../utils/ArrayUtils';

const postKeys = {
  all: (communityId: number) => [COMMUNITIES_KEY, communityId, POSTS_QUERY_KEY],
  lists: (communityId: number) => [...postKeys.all(communityId), LIST],
  list: (communityId: number, postFilter: PostFilter) => [...postKeys.lists(communityId), { postFilter }],
  details: (communityId: number) => [...postKeys.all(communityId), DETAIL],
  detail: (communityId: number, postId: number) => [...postKeys.details(communityId), postId],
};

const commentKeys = {
  all: (communityId: number, postId: number) => [COMMUNITIES_KEY, communityId, COMMENTS_QUERY_KEY, postId],
  lists: (communityId: number, postId: number) => [...commentKeys.all(communityId, postId), LIST],
};

export enum PostFilter {
  ALL = 'ALL',
  ANNOUNCEMENTS = 'ANNOUNCEMENTS',
}

export const postFilterBadge = (postFilter: PostFilter): string | undefined => {
  if (postFilter === PostFilter.ANNOUNCEMENTS) {
    return '1';
  }
  return undefined;
};

const postTags = (postFilter: PostFilter): PostTag[] => {
  if (postFilter === PostFilter.ANNOUNCEMENTS) {
    return [PostTag.Announcement];
  }
  return [];
};

export const usePrefetchPostAggregates = () => {
  const currentMember = useCurrentActiveMember();
  const communityId = currentMember?.communityId;
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!communityId) {
      return;
    }
    queryClient.prefetchInfiniteQuery(postKeys.list(communityId, PostFilter.ALL), ({ pageParam }) =>
      memberApi.listPostAggregatesPaginated({ communityId: communityId, page: pageParam ?? 0 })
    );
  }, [communityId, queryClient]);
};

export const usePostsQuery = (postFilter: PostFilter): UseInfiniteQueryResult<PagePostAggregate> => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const currentMember = useCurrentMember()!;
  const queryClient = useQueryClient();
  const query = useInfiniteQuery(
    postKeys.list(currentMember.communityId, postFilter),
    ({ pageParam }) =>
      memberApi.listPostAggregatesPaginated({
        communityId: currentMember.communityId,
        page: pageParam ?? 0,
        postTags: postTags(postFilter),
      }),
    {
      getNextPageParam: (lastPage) => (lastPage.number < lastPage.totalPages ? lastPage.number + 1 : undefined),
      onSuccess(data: InfiniteData<PagePostAggregate>) {
        const keyExtractor = (post: PostAggregate) => post.postId;
        for (const post of getInfinitePagesContent(data, keyExtractor)) {
          queryClient.setQueryData<Post | undefined>(
            postKeys.detail(currentMember.communityId, post.postId),
            (oldPost) => {
              return {
                ...oldPost,
                ...post,
              };
            }
          );
          queryClient.setQueryData<PostComment[] | undefined>(
            commentKeys.lists(currentMember.communityId, post.postId),
            (old) => {
              // Override the comment query results.
              return post.comments;
            }
          );
        }
      },
    }
  );
  const isLoadingLatest = (!query.isFetched && query.isFetching) || query.isRefetching;
  const inAppMessagesMutation = useInAppMessagesMutation();
  // Mutate is stable but markMessageTypesAsRead is not. https://github.com/tannerlinsley/react-query/issues/1858
  const markMessageTypesAsRead = inAppMessagesMutation.markMessageTypesAsRead.mutate;
  useEffect(() => {
    if (isLoadingLatest) {
      markMessageTypesAsRead([InAppMessageType.Feed]);
    }
  }, [markMessageTypesAsRead, isLoadingLatest]);
  return query;
};

export const usePostQuery = (postId: number) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const currentMember = useCurrentMember()!;
  return useQuery(
    postKeys.detail(currentMember.communityId, postId),
    () =>
      memberApi.getPost({
        postId: postId,
      }),
    {
      refetchOnWindowFocus: false,
      staleTime: 60 * 1000,
      enabled: !!currentMember,
    }
  );
};

export const usePostCommentsQuery = (postId: number) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const currentMember = useCurrentMember()!;
  return useQuery<PostComment[]>(
    commentKeys.lists(currentMember.communityId, postId),
    async ({ pageParam }) => {
      let list: PostComment[] = [];
      let lastPage: PagePostComment;
      do {
        lastPage = await memberApi.listComments({
          postId: postId,
          page: pageParam ?? 0,
          type: [PostCommentType.Comment, PostCommentType.Ack, PostCommentType.Reaction],
        });
        pageParam = lastPage.number + 1;
        list = [...list, ...lastPage.content];
      } while (lastPage.number < lastPage.totalPages);
      return dedupe(list, (it) => it.postCommentId);
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 60 * 1000,
      enabled: !!currentMember,
    }
  );
};

export const usePostMutation = () => {
  const queryClient = useQueryClient();
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const currentMember = useCurrentMember()!;

  const createPostMutation = useMutation((request: CreatePostRequest) => memberApi.createPost(request), {
    onSuccess: async (response: Post) => {
      // Optimistically update the post.
      queryClient.setQueryData(postKeys.detail(currentMember.communityId, response.postId), response);
      // Optimistically update the feed.
      const postAggregate: PostAggregate = { ...response, comments: [] };
      queryClient.setQueryData<InfiniteData<PagePostAggregate> | undefined>(
        postKeys.list(currentMember.communityId, PostFilter.ALL),
        (old) => insertToInfinitePages(old, postAggregate)
      );
      if (postAggregate.postTags.includes(PostTag.Announcement)) {
        queryClient.setQueryData<InfiniteData<PagePostAggregate> | undefined>(
          postKeys.list(currentMember.communityId, PostFilter.ANNOUNCEMENTS),
          (old) => insertToInfinitePages(old, postAggregate)
        );
      }
      // Invalidate the feed.
      queryClient.invalidateQueries(postKeys.lists(currentMember.communityId));
    },
  });
  const updatePostMutation = useMutation((request: UpdatePostRequest) => memberApi.updatePost(request), {
    onSuccess: async (response: Post) => {
      // Optimistically update the post.
      queryClient.setQueryData(postKeys.detail(currentMember.communityId, response.postId), response);
      // Invalidate all feed queries.
      queryClient.invalidateQueries(postKeys.lists(currentMember.communityId));
    },
  });
  const hidePostMutation = useMutation((request: HidePostRequest) => managementApi.hidePost(request), {
    onSuccess: async (response: Post) => {
      // Optimistically update the post.
      queryClient.setQueryData(postKeys.detail(currentMember.communityId, response.postId), response);
      // Invalidate all feed queries.
      queryClient.invalidateQueries(postKeys.lists(currentMember.communityId));
    },
  });
  return {
    createPostMutation,
    updatePostMutation,
    hidePostMutation,
  };
};

export const usePostCommentMutation = () => {
  const queryClient = useQueryClient();
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const currentMember = useCurrentMember()!;

  const commonInvalidation = async (response: PostComment) => {
    // Invalidate the same page first then invalidate the list.
    await Promise.all([
      queryClient.invalidateQueries(postKeys.detail(currentMember.communityId, response.postId)),
      queryClient.invalidateQueries(commentKeys.lists(currentMember.communityId, response.postId)),
    ]);
  };

  const createPostCommentMutation = useMutation((request: CreateCommentRequest) => memberApi.createComment(request), {
    onSuccess: (response: PostComment) => {
      // Optimistically update the comments query.
      queryClient.setQueriesData<PostComment[] | undefined>(
        commentKeys.lists(currentMember.communityId, response.postId),
        (old) => insertToList(old, response, true)
      );
      commonInvalidation(response);
    },
  });
  const createAckPostCommentMutation = useMutation(
    (request: CreateAckCommentRequest) => memberApi.createAckComment(request),
    {
      onSuccess: (response: PostComment) => {
        // Optimistically update the comments query.
        queryClient.setQueriesData<PostComment[] | undefined>(
          commentKeys.lists(currentMember.communityId, response.postId),
          (old) => insertToList(old, response, true)
        );
        commonInvalidation(response);
      },
    }
  );
  const createReactionPostCommentMutation = useMutation(
    (request: CreateReactionCommentRequest) => memberApi.createReactionComment(request),
    {
      onSuccess: (response: PostComment) => {
        // Optimistically update the comments query.
        queryClient.setQueriesData<PostComment[] | undefined>(
          commentKeys.lists(currentMember.communityId, response.postId),
          (old) => insertToList(old, response, true)
        );
        commonInvalidation(response);
      },
    }
  );
  const updatePostCommentMutation = useMutation((request: UpdateCommentRequest) => memberApi.updateComment(request), {
    onSuccess: (response: PostComment) => {
      // Optimistically update the comments query.
      queryClient.setQueriesData<PostComment[] | undefined>(
        commentKeys.lists(currentMember.communityId, response.postId),
        (old) =>
          replaceInList(
            old,
            (it) => it.postCommentId === response.postCommentId,
            () => response
          )
      );
      commonInvalidation(response);
    },
  });
  return {
    createPostCommentMutation,
    createAckPostCommentMutation,
    createReactionPostCommentMutation,
    updatePostCommentMutation,
  };
};
